import React, { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Search, ScrollText, Clock, Flame, Users, Building2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { formatRange, formatDate } from "@/lib/utils_gg";
import StatusBadge, { waitingLabel } from "@/components/StatusBadge";
import { TYPES } from "@/lib/constants";

const STATUS_TABS = ["pending", "changes_pending", "changes_requested", "approved", "rejected", "unpublished", "archived", "all"];

function OnFireSetting() {
  const { t, onFireQuorum, setOnFireQuorum } = useApp();
  const [val, setVal] = useState(String(onFireQuorum));
  const [busy, setBusy] = useState(false);
  useEffect(() => setVal(String(onFireQuorum)), [onFireQuorum]);
  const valid = /^\d+$/.test(val) && +val >= 1 && +val <= 9999;
  const save = async e => {
    e.preventDefault(); if (!valid || busy) return;
    setBusy(true);
    try { const { data } = await api.put("/admin/settings", { on_fire_quorum: +val }); setOnFireQuorum(data.on_fire_quorum); toast.success(t("onFireSaved")); }
    catch { toast.error(t("onFireInvalid")); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="flex items-center gap-1.5 text-xs" data-testid="on-fire-setting" title={t("onFireHint")}>
      <label htmlFor="on-fire-quorum" className="eyebrow !text-[10px] text-muted-foreground inline-flex items-center gap-1"><Flame className="w-3.5 h-3.5" />{t("onFireQuorum")}</label>
      <input id="on-fire-quorum" type="number" inputMode="numeric" min={1} max={9999} step={1} value={val} onChange={e => setVal(e.target.value)}
        className={`gg-input !py-1.5 !px-2 w-20 text-xs ${val !== "" && !valid ? "!border-destructive" : ""}`} aria-invalid={!valid} data-testid="on-fire-quorum-input" />
      <button type="submit" disabled={!valid || busy || +val === onFireQuorum} className="gg-btn !py-1.5 !px-2 text-xs disabled:opacity-40" data-testid="on-fire-quorum-save">{t("save")}</button>
    </form>
  );
}

export default function AdminDashboard() {
  const { user, t, lang, checkingAuth } = useApp();
  const [sp, setSp] = useSearchParams();
  const status = sp.get("status") || "pending";
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [f, setF] = useState({ q: "", organizer: "", country: "", event_type: "", date_from: "", date_to: "" });

  const load = () => {
    const params = { status, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
    api.get("/admin/events", { params }).then(r => { setItems(r.data.items); setTotal(r.data.total); });
    api.get("/admin/stats").then(r => setStats(r.data));
  };
  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-line */ }, [status, user?.role]);
  if (checkingAuth) return null;
  if (!user) return <Navigate to="/auth" />;
  if (user.role !== "admin") return <div className="p-8">403 — Admin only.</div>;

  const tabLabel = { pending: t("statPending"), changes_pending: t("statChangesPending"), changes_requested: t("statChangesRequested"),
    approved: t("statApproved"), rejected: t("rejected"), unpublished: t("unpublished"), archived: t("archive"), all: t("allEvents") };
  const statCells = stats ? [["pending", stats.pending, t("statPending")], ["changes_requested", stats.changes_requested, t("statChangesRequested")],
    ["approved", stats.approved, t("statApproved")], ["rejected", stats.rejected, t("rejected")], ["changes_pending", stats.changes_pending, t("statChangesPending")]] : [];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8" data-testid="admin-dashboard">
      <div className="flex items-end justify-between gap-3 flex-wrap border-b border-foreground pb-3">
        <div><div className="eyebrow text-primary">{t("adminOverview")} · {t("eventModeration")}</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tighter">{t("admin")}</h1></div>
        <div className="flex items-center gap-2">
          <OnFireSetting />
          <Link to="/admin/users" className="gg-btn !py-2 !px-3 text-xs" data-testid="admin-users-link"><Users className="w-4 h-4" />{t("userManagement")}</Link>
          <Link to="/admin/crews" className="gg-btn !py-2 !px-3 text-xs" data-testid="admin-crews-link"><Building2 className="w-4 h-4" />{t("crewManagement")}</Link>
          <Link to="/admin/audit" className="gg-btn !py-2 !px-3 text-xs" data-testid="admin-audit-link"><ScrollText className="w-4 h-4" />{t("auditLog")}</Link>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 border border-foreground divide-x divide-y md:divide-y-0 divide-foreground" data-testid="admin-stats">
        {statCells.map(([st, n, label], i) => (
          <button key={i} onClick={() => setSp({ status: st })} className="p-3 text-left hover:bg-muted transition-colors" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="font-display text-3xl leading-none">{n}</div><div className="eyebrow mt-1 text-muted-foreground">{label}</div>
          </button>
        ))}
      </div>

      <div className="mt-6 flex gap-1 overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0" data-testid="admin-status-tabs">
        {STATUS_TABS.map(s => (
          <button key={s} data-testid={`admin-tab-${s}`} onClick={() => setSp({ status: s })}
            className={`gg-chip !py-2 !px-3 whitespace-nowrap ${status === s ? "gg-chip-active" : "hover:bg-muted"}`}>
            {tabLabel[s]}{s === "pending" && stats?.pending > 0 && <span className="ml-1.5 opacity-80">({stats.pending})</span>}
          </button>
        ))}
      </div>

      <form onSubmit={e => { e.preventDefault(); load(); }} className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="admin-filters">
        <div className="col-span-2 relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className="gg-input !pl-9 !py-2" placeholder={t("searchAdmin")} value={f.q} onChange={e => setF({ ...f, q: e.target.value })} data-testid="admin-search" /></div>
        <input className="gg-input !py-2" placeholder={t("organizerCol")} value={f.organizer} onChange={e => setF({ ...f, organizer: e.target.value })} data-testid="admin-f-organizer" />
        <input className="gg-input !py-2" placeholder={t("country")} value={f.country} onChange={e => setF({ ...f, country: e.target.value })} data-testid="admin-f-country" />
        <select className="gg-input !py-2" value={f.event_type} onChange={e => setF({ ...f, event_type: e.target.value })} data-testid="admin-f-type">
          <option value="">{t("allTypes")}</option>{TYPES.map(x => <option key={x} value={x}>{t(`types.${x}`)}</option>)}</select>
        <div className="flex gap-1"><input type="date" className="gg-input !py-2 !px-2" value={f.date_from} onChange={e => setF({ ...f, date_from: e.target.value })} data-testid="admin-f-from" />
          <button type="submit" className="gg-btn-primary !py-2 !px-3" data-testid="admin-filter-apply"><Search className="w-4 h-4" /></button></div>
      </form>

      <div className="mt-6 flex items-center justify-between">
        <h2 className="font-display uppercase text-xl md:text-2xl tracking-tight" data-testid="admin-list-title">{status === "pending" ? t("waitingForApproval") : tabLabel[status]}</h2>
        <span className="eyebrow text-muted-foreground" data-testid="admin-total">{total}</span>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block mt-3 border border-foreground overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-events-table">
          <thead className="bg-muted/50 eyebrow text-left"><tr>
            <th className="p-2 w-16"></th><th className="p-2">{t("eventCol")}</th><th className="p-2">{t("date")}</th><th className="p-2">{t("location")}</th>
            <th className="p-2">{t("eventType")}</th><th className="p-2">{t("organizerCol")}</th><th className="p-2">{t("submitted")}</th><th className="p-2">{t("statusCol")}</th></tr></thead>
          <tbody className="divide-y divide-foreground">
            {items.map(e => (
              <tr key={e.event_id} className="hover:bg-muted/60 cursor-pointer" onClick={() => window.location.assign(`/admin/events/${e.event_id}`)} data-testid={`admin-row-${e.event_id}`}>
                <td className="p-2"><div className="w-14 h-10 overflow-hidden border border-foreground bg-muted"><img src={e.main_image} alt="" className="gg-flyer" /></div></td>
                <td className="p-2"><Link to={`/admin/events/${e.event_id}`} className="font-display uppercase text-base hover:text-primary" data-testid={`admin-open-${e.event_id}`}>{e.name}</Link>
                  <div className="text-xs text-muted-foreground">{e.genres.slice(0, 3).map(g => t(`genres.${g}`)).join(" · ")}</div></td>
                <td className="p-2 whitespace-nowrap">{formatRange(e, lang)}</td>
                <td className="p-2">{e.city}, {e.country}</td>
                <td className="p-2">{t(`types.${e.event_type}`)}</td>
                <td className="p-2">{e.organizer_name}</td>
                <td className="p-2 whitespace-nowrap text-xs">{formatDate((e.submitted_at || e.created_at || "").slice(0, 10), lang)}
                  {e.mod_status === "pending" && <span className="ml-1 text-primary inline-flex items-center gap-0.5" data-testid={`waiting-${e.event_id}`}><Clock className="w-3 h-3" />{waitingLabel(e.waiting_hours)}</span>}</td>
                <td className="p-2"><div className="flex flex-col gap-1 items-start"><StatusBadge status={e.mod_status} />
                  {e.has_pending_changes && <span className="eyebrow !text-[10px] px-2 py-1 border border-primary text-primary">{t("changesPending")}</span>}
                  {e.public_status && e.public_status !== "scheduled" && <span className="eyebrow !text-[10px]">{t(`public.${e.public_status}`)}</span>}</div></td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={8} className="p-8 text-center eyebrow">—</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden mt-3 space-y-2" data-testid="admin-events-cards">
        {items.map(e => (
          <Link to={`/admin/events/${e.event_id}`} key={e.event_id} className="gg-card flex gap-3 p-3" data-testid={`admin-card-${e.event_id}`}>
            <div className="w-20 h-16 shrink-0 overflow-hidden border border-foreground bg-muted"><img src={e.main_image} alt="" className="gg-flyer" /></div>
            <div className="min-w-0 flex-1">
              <div className="font-display uppercase leading-tight truncate">{e.name}</div>
              <div className="text-xs text-muted-foreground">{formatRange(e, lang)} · {e.city}</div>
              <div className="text-xs text-muted-foreground">{e.organizer_name}</div>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap"><StatusBadge status={e.mod_status} />
                <span className="text-[11px] text-muted-foreground">{formatDate((e.submitted_at || e.created_at || "").slice(0, 10), lang)}{e.mod_status === "pending" && ` · ${waitingLabel(e.waiting_hours)} ${t("waiting")}`}</span></div>
            </div>
          </Link>
        ))}
        {!items.length && <div className="p-8 text-center eyebrow border border-dashed border-foreground">—</div>}
      </div>
      <OwnershipClaims />
      <AdminQueue kind="support" />
      <AdminQueue kind="reports" />
    </div>
  );
}

function OwnershipClaims() {
  const { t, lang } = useApp();
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/claims").then(r => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState("");
  const act = async (id, action, n = "") => {
    try { await api.post(`/admin/claims/${id}/action`, { action, note: n }); toast.success(action === "approve" ? t("claimApproved") : t("claimRejected")); setRejecting(null); setNote(""); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const open = items.filter(c => c.status === "open");
  return (
    <section className="mt-12" data-testid="admin-claims">
      <h2 className="font-display uppercase text-2xl tracking-tight border-b border-foreground pb-2 flex items-center gap-2">{t("ownershipRequests")} <span className="eyebrow text-muted-foreground">({open.length})</span></h2>
      {!open.length && <p className="text-sm text-muted-foreground mt-3">{t("noOpenRequests")}</p>}
      <ul className="divide-y divide-foreground/30">
        {open.map(c => (
          <li key={c.claim_id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`claim-${c.claim_id}`}>
            <div className="min-w-0 flex-1">
              <Link to={`/events/${c.event_slug}`} className="font-display uppercase text-lg hover:text-primary">{c.event_name} →</Link>
              <div className="text-xs text-muted-foreground"><Link to={`/users/${c.user_id}`} className="underline">{c.user_name}</Link> · {c.user_email} · {t("requestedOn")} {new Date(c.created_at).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB")}</div>
              {c.promoter_name && <div className="text-xs mt-1" data-testid={`claim-promoter-${c.claim_id}`}><span className="eyebrow !text-[10px] text-muted-foreground mr-1">{t("promoter")}</span>{c.promoter_name}</div>}
              {c.message && <p className="text-sm mt-1" data-testid={`claim-message-${c.claim_id}`}>{c.message}</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setRejecting(c.claim_id); setNote(""); }} className="gg-btn !py-2 !px-3 text-xs" data-testid={`claim-reject-${c.claim_id}`}>{t("reject")}</button>
              <button onClick={() => act(c.claim_id, "approve")} className="gg-btn-primary !py-2 !px-3 text-xs" data-testid={`claim-approve-${c.claim_id}`}>{t("approve")}</button>
            </div>
            {rejecting === c.claim_id && (
              <div className="basis-full mt-2 flex flex-col sm:flex-row gap-2" data-testid={`claim-reject-form-${c.claim_id}`}>
                <input className="gg-input !py-2 text-sm flex-1" placeholder={t("claimRejectNote")} value={note} onChange={e => setNote(e.target.value)} data-testid={`claim-reject-note-${c.claim_id}`} />
                <div className="flex gap-2"><button onClick={() => setRejecting(null)} className="gg-btn !py-2 !px-3 text-xs">{t("cancel")}</button><button onClick={() => act(c.claim_id, "reject", note)} className="gg-btn-primary !py-2 !px-3 text-xs" data-testid={`claim-reject-confirm-${c.claim_id}`}>{t("reject")}</button></div>
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-muted-foreground" data-testid="claims-mail-hint">{t("claimsMailHint")}</p>
    </section>
  );
}

function AdminQueue({ kind }) {
  const { t, lang } = useApp();
  const [items, setItems] = useState([]);
  const [all, setAll] = useState(false);
  const load = () => api.get(`/admin/${kind}`).then(r => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  const setStatus = async (id, status) => {
    try { await api.post(`/admin/${kind}/${id}/status`, { status }); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const shown = all ? items : items.filter(x => (x.status || "open") === "open");
  const fmt = s => new Date(s).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { day: "2-digit", month: "short", year: "numeric" });
  return (
    <section className="mt-12" data-testid={`admin-${kind}`}>
      <div className="flex items-center justify-between border-b border-foreground pb-2">
        <h2 className="font-display uppercase text-2xl tracking-tight flex items-center gap-2">{t(kind === "support" ? "supportRequests" : "userReports")} <span className="eyebrow text-muted-foreground">({items.filter(x => (x.status || "open") === "open").length})</span></h2>
        <button onClick={() => setAll(v => !v)} className="eyebrow hover:text-primary" data-testid={`admin-${kind}-toggle`}>{all ? t("onlyOpen") : t("showAll")}</button>
      </div>
      {!shown.length && <p className="text-sm text-muted-foreground mt-3">{t("noItems")}</p>}
      <ul className="divide-y divide-foreground/30">
        {shown.map(x => {
          const id = x.ticket_id || x.report_id; const st = x.status || "open";
          return (
            <li key={id} className="py-3 flex flex-wrap items-start gap-3" data-testid={`${kind}-item-${id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`gg-chip !text-[10px] ${st === "open" ? "gg-chip-active" : ""}`}>{t(st)}</span>
                  {kind === "support" ? <span className="eyebrow !text-[10px] text-primary">{t(`helpCat.${x.category}`)}</span> : <span className="eyebrow !text-[10px] text-primary">{t("report")}</span>}
                  <span className="text-xs text-muted-foreground">{fmt(x.created_at)} · {id}</span>
                </div>
                {kind === "support" ? (<>
                  <div className="font-display uppercase text-lg mt-1">{x.subject}</div>
                  <p className="text-sm whitespace-pre-wrap mt-1">{x.description}</p>
                  <div className="text-xs text-muted-foreground mt-1"><a href={`mailto:${x.email}`} className="underline">{x.email}</a>{x.user_id && <> · <Link to={`/users/${x.user_id}`} className="underline">{x.user_id}</Link></>}{x.event_link && <> · <a href={x.event_link} className="underline" target="_blank" rel="noreferrer">Event ↗</a></>}{x.attachment_url && <> · <a href={x.attachment_url} className="underline" target="_blank" rel="noreferrer">{t("fileAttached")} ↗</a></>}</div>
                </>) : (<>
                  <div className="font-display uppercase text-lg mt-1"><Link to={`/users/${x.target_id}`} className="hover:text-primary">{x.target_name}</Link> <span className="text-xs text-muted-foreground font-sans normal-case">{x.target_email}</span></div>
                  <p className="text-sm whitespace-pre-wrap mt-1">{x.reason}</p>
                  <div className="text-xs text-muted-foreground mt-1">{t("reportedBy")}: <Link to={`/users/${x.reporter_id}`} className="underline">{x.reporter_name}</Link>{x.conversation_id && <> · <Link to={`/messages/${x.conversation_id}`} className="underline">{t("messages")}</Link></>}</div>
                  <ModerationTools userId={x.target_id} reportId={x.report_id} />
                </>)}
              </div>
              <div className="flex gap-2">
                {kind === "support" ? (st === "open" ? <button onClick={() => setStatus(id, "closed")} className="gg-btn-primary !py-2 !px-3 text-xs" data-testid={`support-close-${id}`}>{t("markClosed")}</button> : <button onClick={() => setStatus(id, "open")} className="gg-btn !py-2 !px-3 text-xs">{t("reopen")}</button>)
                  : (st === "open" ? <><button onClick={() => setStatus(id, "dismissed")} className="gg-btn !py-2 !px-3 text-xs" data-testid={`report-dismiss-${id}`}>{t("dismiss")}</button><button onClick={() => setStatus(id, "resolved")} className="gg-btn-primary !py-2 !px-3 text-xs" data-testid={`report-resolve-${id}`}>{t("resolve")}</button></> : <button onClick={() => setStatus(id, "open")} className="gg-btn !py-2 !px-3 text-xs">{t("reopen")}</button>)}
              </div>
            </li>);
        })}
      </ul>
    </section>
  );
}

function ModerationTools({ userId, reportId }) {
  const { t, lang } = useApp();
  const [hist, setHist] = useState(null);
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const load = () => api.get(`/admin/users/${userId}/moderation`).then(r => setHist(r.data)).catch(() => {});
  useEffect(() => { load(); }, [userId]); // eslint-disable-line
  const act = async (action) => {
    if (action !== "lift" && !note.trim()) { toast.error(t("moderationNote")); return; }
    try { await api.post(`/admin/users/${userId}/moderate`, { action, note, report_id: reportId }); toast.success(t("saved")); setNote(""); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const fmt = s => new Date(s).toLocaleString(lang === "de" ? "de-DE" : "en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="mt-2 border-t border-foreground/30 pt-2" data-testid={`moderation-${userId}`}>
      <div className="flex flex-wrap items-center gap-2">
        {hist?.suspended && <span className="gg-chip gg-status-cancelled !text-[10px]" data-testid={`suspended-badge-${userId}`}>{t("suspended")}</span>}
        <input className="gg-input !py-1.5 text-xs flex-1 min-w-[180px]" placeholder={t("moderationNote")} value={note} onChange={e => setNote(e.target.value)} data-testid={`mod-note-${userId}`} />
        <button onClick={() => act("warn")} className="gg-btn !py-1.5 !px-2.5 text-xs" data-testid={`mod-warn-${userId}`}>{t("warnUser")}</button>
        {hist?.suspended
          ? <button onClick={() => act("lift")} className="gg-btn-primary !py-1.5 !px-2.5 text-xs" data-testid={`mod-lift-${userId}`}>{t("liftSuspension")}</button>
          : <button onClick={() => act("suspend")} className="gg-btn !py-1.5 !px-2.5 text-xs gg-status-cancelled" data-testid={`mod-suspend-${userId}`}>{t("suspendUser")}</button>}
        <button onClick={() => setOpen(v => !v)} className="eyebrow !text-[10px] hover:text-primary" data-testid={`mod-history-toggle-${userId}`}>{t("moderationHistory")} ({hist?.items?.length ?? 0})</button>
      </div>
      {open && hist && (
        <ul className="mt-2 text-xs divide-y divide-foreground/20" data-testid={`mod-history-${userId}`}>
          {hist.items.map(i => <li key={i.log_id} className="py-1.5 flex flex-wrap gap-x-3"><span className="font-display uppercase">{t(`modAction.${i.kind}`)}</span><span className="text-muted-foreground">{fmt(i.created_at)}{i.admin_name ? ` · ${i.admin_name}` : ""}{i.status ? ` · ${t(i.status)}` : ""}</span>{i.note && <span className="basis-full text-foreground/80">{i.note}</span>}</li>)}
        </ul>
      )}
    </div>
  );
}
